// src/components/TopBar.jsx
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import logo from "../assets/images/logo.png";
import { API_BASE_URL } from "../utils/api";

const TopBar = ({
  onBack,
  activeLoading,
  title,
  onLogoClick,
  onCartClick,
  isMobile,
}) => {
  const [count, setCount] = useState(0);
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  // ------- helpers -------
  const readGuestCount = () => {
    try {
      const raw = localStorage.getItem("cart");
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.length : 0;
    } catch {
      return 0;
    }
  };

  // Normalize various API shapes to an array of carts
  const extractCarts = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (payload?.data && typeof payload.data === "object") return [payload.data];
    if (payload && typeof payload === "object" && Array.isArray(payload.items)) return [payload];
    return [];
  };

  // Count items with rules:
  // - If cart.total_quantity > 0, use it.
  // - Else, sum items[].quantity but treat 0/undefined as 1.
  const deriveCount = (payload) => {
    const carts = extractCarts(payload);
    return carts.reduce((sum, cart) => {
      const tq = Number(cart?.total_quantity);
      if (tq > 0) return sum + tq;

      const items = Array.isArray(cart?.items) ? cart.items : [];
      const itemsSum = items.reduce((s, it) => {
        const q = Number(it?.quantity);
        return s + (q > 0 ? q : 1);
      }, 0);
      return sum + itemsSum;
    }, 0);
  };

  const fetchAuthedCount = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/carts/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data && data.status === false) throw new Error("Unauthenticated");
      setCount(deriveCount(data));
    } catch {
      // fall back to guest count if auth fails/401/shape issues
      setCount(readGuestCount());
    }
  };

  // initial + token changes
  useEffect(() => {
    if (!token) {
      setCount(readGuestCount());
      return;
    }
    fetchAuthedCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // react to localStorage changes across tabs (guest)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "cart" && !token) setCount(readGuestCount());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [token]);

  // allow manual recount from anywhere:
  // window.dispatchEvent(new Event("cart:recount"))
  useEffect(() => {
    const handler = () => {
      if (token) fetchAuthedCount();
      else setCount(readGuestCount());
    };
    window.addEventListener("cart:recount", handler);
    return () => window.removeEventListener("cart:recount", handler);
  }, [token]);

  const showBadge = Number(count) > 0;

  const handleBack = () => {
    try {
      sessionStorage.setItem("bs_preserve_title", "1");
      sessionStorage.setItem("bs_use_cache", "1");
    } catch {}
    onBack?.({ preserveTitle: true, useCache: true });
  };

  return (
    <div className="topbar">
      {/* Back */}
      <motion.button
        className="back-pill"
        onClick={handleBack}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.98 }}
        style={{ position: "absolute", height: "40px", top: "30px", left: "80px" }}
        disabled={activeLoading}
        type="button"
      >
        <span>ارجع للخلف</span>
      </motion.button>

      {/* Cart with badge */}
      <button
        className="cart-icon-btn has-badge"
        aria-label="سلة التسوق"
        onClick={onCartClick}
        title="سلة التسوق"
        type="button"
      >
        {isMobile ? <i className="bi bi-cart3" /> : "سلة التسوق"}
        {showBadge && <span className="cart-badge">{count}</span>}
      </button>

      {/* Title */}
      <h1 className="screen-title" dir="rtl">
        {title}
      </h1>

      {/* Logo */}
      <img
        src={logo}
        alt="PARTECH"
        onClick={onLogoClick}
        role="button"
        tabIndex={0}
        style={{
          position: "absolute",
          height: "40px",
          top: "30px",
          right: "80px",
          cursor: "pointer",
          outline: "none",
        }}
      />
    </div>
  );
};

export default TopBar;
